/**
 * Auth-aware fetch helper for the feature's TanStack Query hooks.
 *
 * Mirrors the helpers in `feature-epi-chat/src/lib/api/authFetch.ts` and
 * `feature-analytical-variables/src/lib/api/fetchers.ts`. Three near-identical
 * copies is the explicit signal it's time to promote — the original two copies
 * already note "promote to `@verantos-dev/core-utils` once a third feature
 * wants the same shape." Tracked as a follow-up so this PR stays scoped.
 */

// TODO: cleanup with concept_group_prevalence_preview ff — promote authFetch
// to @verantos-dev/core-utils as part of cleanup; three copies now exist
// across feature-epi-chat, feature-analytical-variables, and this lib.

import { getToken } from '@verantos-dev/core-auth';
import { getBaseApiUrl, getGatewayApiVersion } from '@verantos-dev/core-constants';

export class AuthFetchError extends Error {
  constructor(
    public readonly status: number,
    public readonly statusText: string,
    public readonly body: string
  ) {
    super(`API error: ${status} ${statusText}`);
    this.name = 'AuthFetchError';
  }
}

export async function authFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const baseUrl = `${getBaseApiUrl()}/${getGatewayApiVersion()}`;
  const token = await getToken();

  const response = await fetch(`${baseUrl}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  });

  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new AuthFetchError(response.status, response.statusText, body);
  }

  return response.json() as Promise<T>;
}
